import React, { useMemo, useState } from "react";

/**
 * iter63 Phase 1C · color × size availability grid.
 *
 * Renders a matrix of dropdowns for each color/size combination. The parent
 * owns the `colors` + `sizes` arrays; this component owns the variants map
 * and calls `onChange(variantsArray)` whenever the matrix mutates.
 */
const AVAILABILITY = [
  { value: "available", label: "In Stock" },
  { value: "limited", label: "Limited" },
  { value: "out_of_stock", label: "Out" },
  { value: "discontinued", label: "Discontinued" },
];

export default function VariantGrid({ colors = [], sizes = [], variants = [], onChange }) {
  // Store variants keyed by "color::size" for fast lookup during render.
  const [map, setMap] = useState(() => {
    const m = new Map();
    for (const v of variants) m.set(`${v.color}::${v.size}`, v);
    return m;
  });

  const setCell = (color, size, patch) => {
    const key = `${color}::${size}`;
    const prev = map.get(key) || { color, size, availability: "available" };
    const next = { ...prev, ...patch };
    const newMap = new Map(map);
    newMap.set(key, next);
    setMap(newMap);
    onChange?.(Array.from(newMap.values()));
  };

  const clearCell = (color, size) => {
    const key = `${color}::${size}`;
    if (!map.has(key)) return;
    const newMap = new Map(map);
    newMap.delete(key);
    setMap(newMap);
    onChange?.(Array.from(newMap.values()));
  };

  const activeCount = useMemo(
    () => Array.from(map.values()).filter((v) => v.availability && v.availability !== "discontinued").length,
    [map],
  );

  if (colors.length === 0 || sizes.length === 0) {
    return (
      <div className="text-xs text-[#7A7A7A] italic" data-testid="variant-grid-empty">
        Add at least one color and one size to build the variant grid.
      </div>
    );
  }

  return (
    <div data-testid="variant-grid">
      <div className="text-[10px] uppercase tracking-widest text-[#7A7A7A] mb-2">
        {activeCount} of {colors.length * sizes.length} variants stocked
      </div>
      <div className="overflow-x-auto border border-[#E5E3D8] bg-white">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="bg-[#F9F8F4]">
              <th className="p-2 text-left text-[10px] uppercase tracking-widest text-[#7A7A7A] border-b border-[#E5E3D8]">Color · Size</th>
              {sizes.map((s) => (
                <th key={s} className="p-2 text-left text-[10px] uppercase tracking-widest text-[#1A422D] border-b border-[#E5E3D8]" data-testid={`variant-header-${s}`}>{s}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {colors.map((c) => (
              <tr key={c} className="border-t border-[#F0EDE0]">
                <td className="p-2 text-[#1A422D] font-medium" data-testid={`variant-row-${c}`}>{c}</td>
                {sizes.map((s) => {
                  const key = `${c}::${s}`;
                  const cell = map.get(key);
                  return (
                    <td key={s} className="p-2" data-testid={`variant-cell-${c}-${s}`}>
                      <div className="flex items-center gap-1">
                        <select
                          value={cell?.availability || ""}
                          onChange={(e) => {
                            if (!e.target.value) clearCell(c, s);
                            else setCell(c, s, { availability: e.target.value });
                          }}
                          data-testid={`variant-avail-${c}-${s}`}
                          className="border border-[#E5E3D8] text-xs px-1 py-1 focus:outline-none focus:border-[#B59A5A] bg-white"
                        >
                          <option value="">—</option>
                          {AVAILABILITY.map((a) => (
                            <option key={a.value} value={a.value}>{a.label}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={cell?.sku || ""}
                          onChange={(e) => setCell(c, s, { sku: e.target.value })}
                          placeholder="SKU"
                          data-testid={`variant-sku-${c}-${s}`}
                          className="border border-[#E5E3D8] text-xs px-1 py-1 w-16 focus:outline-none focus:border-[#B59A5A]"
                        />
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
